// frontend/src/modules/folha/pages/AdiantamentoSalarial.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEye, FaEdit, FaTrash } from 'react-icons/fa';

function AdiantamentoSalarial({ usuario, isDark = false }) {
  const navigate = useNavigate();
  const [adiantamentos, setAdiantamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const token = localStorage.getItem('token');
  const empresaId = usuario?.empresa_id || 1;

  // ============================================
  // FUNÇÕES AUXILIARES
  // ============================================
  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'APROVADO': return '#28a745';
      case 'REPROVADO': return '#dc3545';
      case 'PAGO': return '#17a2b8';
      default: return '#ffc107';
    }
  };

  // ============================================
  // BUSCAR DADOS
  // ============================================
  const fetchAdiantamentos = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/adiantamento/empresa/${empresaId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erro ao buscar adiantamentos');
      const data = await response.json();
      console.log('📦 Adiantamentos carregados:', data);
      setAdiantamentos(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdiantamentos();
  }, [empresaId]);

  // ============================================
  // FUNÇÕES DE NAVEGAÇÃO
  // ============================================
  const handleVisualizar = (adiantamento) => {
    console.log('🖱️ Clicou em visualizar:', adiantamento);
    
    if (!adiantamento) {
      console.error('Adiantamento é null/undefined');
      alert('Erro: Dados do adiantamento não encontrados');
      return;
    }

    if (!adiantamento.id) {
      console.error('ID do adiantamento não encontrado', adiantamento);
      alert('Erro: ID do adiantamento não encontrado');
      return;
    }

    console.log('🔍 Navegando para adiantamento ID:', adiantamento.id);
    navigate(`/adiantamento/${adiantamento.id}`);
  };

  const handleNovo = () => {
    navigate('/adiantamento/novo');
  };

  const handleEditar = (adiantamento) => {
    if (!adiantamento || !adiantamento.id) {
      alert('Erro: ID do adiantamento não encontrado');
      return;
    }
    navigate(`/adiantamento/editar/${adiantamento.id}`);
  };

  const handleExcluir = async (id) => {
    if (!id) {
      alert('Erro: ID do adiantamento não encontrado');
      return;
    }
    
    if (!window.confirm('Tem certeza que deseja excluir este adiantamento?')) return;
    
    try {
      const response = await fetch(`/api/adiantamento/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erro ao excluir');
      alert('Adiantamento excluído com sucesso!');
      fetchAdiantamentos();
    } catch (err) {
      alert('Erro ao excluir: ' + err.message);
    }
  };

  // ============================================
  // ESTILOS
  // ============================================
  const colors = {
    background: isDark ? '#121212' : '#f8f9fa',
    card: isDark ? '#1e1e1e' : '#ffffff',
    text: isDark ? '#e9ecef' : '#212529',
    border: isDark ? '#333333' : '#dee2e6',
    textSecondary: isDark ? '#adb5bd' : '#6c757d'
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px', color: colors.text }}>🔄 Carregando...</div>;
  }

  return (
    <div style={{
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto',
      backgroundColor: colors.background,
      minHeight: '100vh'
    }}>
      {/* Cabeçalho */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <div>
          <h2 style={{ color: colors.text }}>💰 Adiantamentos Salariais</h2>
          <p style={{ color: colors.textSecondary }}>Gerencie os adiantamentos dos funcionários</p>
        </div>
        <button
          onClick={handleNovo}
          style={{
            padding: '10px 20px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <FaPlus /> Novo Adiantamento
        </button>
      </div>

      {error && (
        <div style={{
          backgroundColor: isDark ? '#3a1a1a' : '#ffebee',
          padding: '10px',
          borderRadius: '4px',
          color: isDark ? '#e8b8b8' : '#c62828',
          marginBottom: '15px',
          border: isDark ? '1px solid #5a2a2a' : '1px solid #ffcdd2'
        }}>
          ❌ {error}
        </div>
      )}

      {/* Lista de Adiantamentos */}
      <div style={{
        backgroundColor: colors.card,
        borderRadius: '8px',
        padding: '20px',
        border: isDark ? `1px solid ${colors.border}` : 'none',
        boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
        overflowX: 'auto'
      }}>
        {adiantamentos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: colors.textSecondary }}>
            Nenhum adiantamento encontrado
          </div>
        ) : (
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            color: colors.text,
            fontSize: '14px'
          }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
                <th style={{ padding: '10px', textAlign: 'left' }}>Funcionário</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>Mês</th>
                <th style={{ padding: '10px', textAlign: 'right' }}>Valor</th>
                <th style={{ padding: '10px', textAlign: 'center' }}>Status</th>
                <th style={{ padding: '10px', textAlign: 'center' }}>Data</th>
                <th style={{ padding: '10px', textAlign: 'center' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {adiantamentos.map((item) => (
                <tr key={item.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <td style={{ padding: '10px' }}>{item.funcionario?.nome_completo || 'N/A'}</td>
                  <td style={{ padding: '10px' }}>{item.mes_referencia}</td>
                  <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold' }}>
                    {formatCurrency(item.valor_solicitado)}
                  </td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      backgroundColor: getStatusColor(item.status),
                      color: 'white',
                      fontSize: '12px'
                    }}>
                      {item.status || 'PENDENTE'}
                    </span>
                  </td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    {formatDate(item.data_solicitacao)}
                  </td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                      <button
                        onClick={() => handleVisualizar(item)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#007bff',
                          cursor: 'pointer',
                          padding: '4px'
                        }}
                        title="Visualizar"
                      >
                        <FaEye size={16} />
                      </button>
                      <button
                        onClick={() => handleEditar(item)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ffc107',
                          cursor: 'pointer',
                          padding: '4px'
                        }}
                        title="Editar"
                      >
                        <FaEdit size={16} />
                      </button>
                      <button
                        onClick={() => handleExcluir(item.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dc3545',
                          cursor: 'pointer',
                          padding: '4px'
                        }}
                        title="Excluir"
                      >
                        <FaTrash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <style>
        {`
          @media (max-width: 768px) {
            .table-responsive {
              overflow-x: auto;
            }
          }
        `}
      </style>
    </div>
  );
}

export default AdiantamentoSalarial;